import Embed from 'quill/blots/embed';

import {isNil} from 'ramda';

import {TEMPLATE_TOKEN} from './TEMPLATE_TOKEN';

export type TemplateTokenValue = {
  code: string;
  label: string;
};

const isTemplateTokenValue = (value: unknown): value is TemplateTokenValue =>
  typeof value === 'object' &&
  value !== null &&
  'code' in value &&
  'label' in value &&
  typeof value.code === 'string' &&
  typeof value.label === 'string' &&
  TEMPLATE_TOKEN.codePattern.test(value.code);

/**
 * An atomic placeholder: the label is its only visible text, `data-code` its identity, and its
 * semantic HTML is the canonical `{{ code }}`.
 */
export class TemplateTokenBlot extends Embed {
  static blotName = TEMPLATE_TOKEN.blotName;
  static tagName = 'span';
  static className = TEMPLATE_TOKEN.className;

  static parse(value: unknown) {
    return isTemplateTokenValue(value) ? value : null;
  }

  static create(value?: unknown) {
    const node = super.create(value);

    if (!(node instanceof HTMLElement) || !isTemplateTokenValue(value)) {
      return node;
    }

    node.dataset.code = value.code;
    node.title = TEMPLATE_TOKEN.toPlaceholder(value.code);
    node.textContent = value.label;

    return node;
  }

  static value(domNode: Node) {
    if (!(domNode instanceof HTMLElement)) {
      return null;
    }

    const code = domNode.dataset.code;

    if (isNil(code) || !TEMPLATE_TOKEN.codePattern.test(code)) {
      return null;
    }

    return {
      code,
      label: (domNode.textContent ?? '').replace(TEMPLATE_TOKEN.guardPattern, ''),
    } satisfies TemplateTokenValue;
  }

  html() {
    const value = TemplateTokenBlot.value(this.domNode);

    return isNil(value) ? '' : TEMPLATE_TOKEN.toPlaceholder(value.code);
  }
}
